import { HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, map, Observable } from 'rxjs';
import { AwsApiClient, PatientApiClient, PatientUploadLinkModel } from 'app/core/api-clients';
import { FormFile } from 'app/core/models/form-file';
import { PatientFileInputModel, PatientFileType } from 'app/core/models';
import { PatientFileUploadState } from './patient-file-upload-state';

@Injectable({ providedIn: 'root' })
export class PatientFilesSaveService {
  private readonly awsClient = inject(AwsApiClient);
  private readonly patientApiClient = inject(PatientApiClient);

  async save(patientId: number, state: PatientFileUploadState | null): Promise<void> {
    if (state === null) {
      return;
    }

    const deleteTasks = state.removedFileIds.map(fileId =>
      firstValueFrom(this.patientApiClient.deleteFile(patientId, fileId))
    );

    await Promise.all(deleteTasks);

    const uploadTasks = Object.entries(state.filesByCategory).flatMap(([category, files]) =>
      files
        .filter(file => file.id === null)
        .map(file => this.uploadFile(patientId, Number(category) as PatientFileType, file))
    );

    await Promise.all(uploadTasks);
  }

  private async uploadFile(patientId: number, category: PatientFileType, file: FormFile): Promise<void> {
    file.inProgress = true;
    file.progress = 0;

    const uploadLink = await firstValueFrom(this.patientApiClient.getUploadLink(patientId, file.name));

    await firstValueFrom(this.uploadToS3(uploadLink, file));

    const input: PatientFileInputModel = {
      fileGuid: uploadLink.fileGuid,
      filename: uploadLink.filename,
      fileSize: file.size,
      fileType: file.type,
      category,
    };

    const savedFile = await firstValueFrom(this.patientApiClient.saveFileInfo(patientId, input));
    file.id = savedFile.id;
    file.s3FileId = savedFile.s3FileId;
    file.fileGUID = savedFile.fileGuid;
    file.inProgress = false;
    file.progress = 100;
  }

  private uploadToS3(uploadLink: PatientUploadLinkModel, file: FormFile): Observable<unknown> {
    return this.awsClient.put(uploadLink.url, file.file, 'application/octet-stream').pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress) {
          file.progress = Math.round((event.loaded * 100) / event.total);
        }

        return event;
      }),
      filter(event => event.type === HttpEventType.Response)
    );
  }
}
